import React, { useState, useEffect } from 'react'
import { useApi } from '../../contexts/ApiContext'
import { useUserId } from '../../hooks/useUserId'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { trackPageView } from '../../utils/eventTracking'
import toast from 'react-hot-toast'
import {
  Bell,
  Check,
  Trash2,
  FileText,
  DollarSign,
  CreditCard,
  AlertCircle,
  CheckCircle,
  Filter,
  RefreshCw
} from 'lucide-react'

const CustomerNotifications = () => {
  const { userId } = useUserId()
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all') // all, unread, read
  const [categoryFilter, setCategoryFilter] = useState('all') // all, claim, payment, document

  // Track page view
  useEffect(() => {
    trackPageView('Customer Notifications', { user_id: userId })
  }, [userId])

  useEffect(() => {
    if (userId) {
      loadNotifications()
    }
  }, [userId])

  const loadNotifications = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('customer_id', userId)
        .neq('notification_status', 'DELETED')
        .order('created_at', { ascending: false })

      if (error) throw error

      setNotifications(data || [])
    } catch (error) {
      console.error('Error loading notifications:', error)
      toast.error('Failed to load notifications')
    } finally {
      setLoading(false)
    }
  }

  const markAsRead = async (notificationId) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({
          is_read: true,
          read_at: new Date().toISOString(),
          notification_status: 'READ',
          updated_at: new Date().toISOString()
        })
        .eq('notification_id', notificationId)

      if (error) throw error

      setNotifications(prev =>
        prev.map(n =>
          n.notification_id === notificationId
            ? { ...n, is_read: true, notification_status: 'READ' }
            : n
        )
      )
      toast.success('Marked as read')
    } catch (error) {
      console.error('Error marking as read:', error)
      toast.error('Failed to mark as read')
    }
  }

  const markAllAsRead = async () => {
    try {
      const unreadIds = notifications.filter(n => !n.is_read).map(n => n.notification_id)
      
      if (unreadIds.length === 0) {
        toast.info('No unread notifications')
        return
      }

      const { error } = await supabase
        .from('notifications')
        .update({
          is_read: true,
          read_at: new Date().toISOString(),
          notification_status: 'READ',
          updated_at: new Date().toISOString()
        })
        .in('notification_id', unreadIds)

      if (error) throw error

      setNotifications(prev =>
        prev.map(n => ({ ...n, is_read: true, notification_status: 'READ' }))
      )
      toast.success(`Marked ${unreadIds.length} notifications as read`)
    } catch (error) {
      console.error('Error marking all as read:', error)
      toast.error('Failed to mark all as read')
    }
  }

  const deleteNotification = async (notificationId) => {
    if (!window.confirm('Delete this notification?')) return

    try {
      const { error } = await supabase
        .from('notifications')
        .update({
          notification_status: 'DELETED',
          updated_at: new Date().toISOString()
        })
        .eq('notification_id', notificationId)

      if (error) throw error

      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId))
      toast.success('Notification deleted')
    } catch (error) {
      console.error('Error deleting notification:', error)
      toast.error('Failed to delete notification')
    }
  }

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.notification_id)
    }

    if (notification.action_url) {
      navigate(notification.action_url + `?user_id=${userId}`)
    }
  }

  const getNotificationIcon = (type) => {
    if (type?.includes('CLAIM')) return <FileText className="h-5 w-5" />
    if (type?.includes('PAYMENT')) return <DollarSign className="h-5 w-5" />
    if (type?.includes('POLICY')) return <CreditCard className="h-5 w-5" />
    return <AlertCircle className="h-5 w-5" />
  }

  const getNotificationColor = (priority) => {
    switch (priority) {
      case 'URGENT': return 'bg-red-100 text-red-600 border-red-200'
      case 'HIGH': return 'bg-orange-100 text-orange-600 border-orange-200'
      default: return 'bg-blue-100 text-blue-600 border-blue-200'
    }
  }

  const formatDateTime = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-IN', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Filter notifications
  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread' && n.is_read) return false
    if (filter === 'read' && !n.is_read) return false
    if (categoryFilter !== 'all' && n.notification_category?.toLowerCase() !== categoryFilter) return false
    return true
  })

  const unreadCount = notifications.filter(n => !n.is_read).length

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading notifications...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Notifications</h1>
          <p className="text-xs sm:text-sm text-gray-600">
            {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={loadNotifications}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            <span className="text-sm">Refresh</span>
          </button>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <CheckCircle className="h-4 w-4" />
              <span className="text-sm">Mark All Read</span>
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="all">All Notifications</option>
              <option value="unread">Unread Only</option>
              <option value="read">Read Only</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Category</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="all">All Categories</option>
              <option value="claim">Claims</option>
              <option value="payment">Payments</option>
              <option value="document">Documents</option>
              <option value="policy">Policies</option>
            </select>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      {filteredNotifications.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
          <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Notifications</h3>
          <p className="text-gray-500">
            {filter !== 'all' || categoryFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'You have no notifications yet'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredNotifications.map((notification) => (
            <div
              key={notification.notification_id}
              className={`bg-white border rounded-xl p-4 transition-all hover:shadow-md ${
                !notification.is_read
                  ? 'border-blue-300 bg-blue-50'
                  : 'border-gray-200'
              }`}
            >
              <div className="flex items-start space-x-4">
                {/* Icon */}
                <div className={`p-3 rounded-full border ${getNotificationColor(notification.notification_priority)}`}>
                  {getNotificationIcon(notification.notification_type)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-semibold text-gray-900">
                          {notification.notification_title}
                        </h3>
                        {!notification.is_read && (
                          <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                            New
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        {notification.notification_message}
                      </p>
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span>{formatDateTime(notification.created_at)}</span>
                        <span className="px-2 py-0.5 bg-gray-100 rounded">
                          {notification.notification_category}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-2 ml-4">
                      {!notification.is_read && (
                        <button
                          onClick={() => markAsRead(notification.notification_id)}
                          className="p-2 hover:bg-green-100 rounded transition-colors"
                          title="Mark as read"
                        >
                          <Check className="h-4 w-4 text-green-600" />
                        </button>
                      )}
                      <button
                        onClick={() => deleteNotification(notification.notification_id)}
                        className="p-2 hover:bg-red-100 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </button>
                    </div>
                  </div>

                  {/* Action Button */}
                  {notification.is_actionable && notification.action_url && (
                    <button
                      onClick={() => handleNotificationClick(notification)}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      {notification.action_label || 'View Details'} →
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default CustomerNotifications


